import { useState } from 'react';
import { useAccessibility } from './AccessibilityProvider';
import {
  Settings,
  Type,
  Eye,
  Minus,
  Plus,
  Moon,
  Zap,
  BookOpen,
  Keyboard,
  Focus,
  RotateCcw,
  X,
} from 'lucide-react';

export function AccessibilityMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const { settings, updateSettings, resetSettings } = useAccessibility();

  const fontSizes = [
    { value: 'small', label: 'Small', px: '14px' },
    { value: 'medium', label: 'Medium', px: '16px' },
    { value: 'large', label: 'Large', px: '18px' },
    { value: 'x-large', label: 'X-Large', px: '20px' },
  ] as const;

  return (
    <>
      {/* Floating Accessibility Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 left-4 z-40 w-14 h-14 bg-gradient-to-br from-blue-600 to-indigo-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center group"
        aria-label="Open Accessibility Settings"
        title="Accessibility Settings"
      >
        <Settings className="w-6 h-6 group-hover:rotate-90 transition-transform duration-300" />
      </button>

      {/* Accessibility Panel */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            role="dialog"
            aria-labelledby="accessibility-title"
            aria-modal="true"
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center">
                  <Settings className="w-6 h-6 text-white" />
                </div>
                <h2 id="accessibility-title" className="text-2xl font-bold text-gray-900">
                  Accessibility Settings
                </h2>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Close Accessibility Settings"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Font Size */}
              <div className="space-y-3">
                <label className="flex items-center gap-2 text-lg font-bold text-gray-900">
                  <Type className="w-5 h-5" />
                  Font Size
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {fontSizes.map(size => (
                    <button
                      key={size.value}
                      onClick={() => updateSettings({ fontSize: size.value })}
                      className={`px-4 py-3 rounded-lg font-medium transition-all ${
                        settings.fontSize === size.value
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      aria-label={`Set font size to ${size.label}`}
                      aria-pressed={settings.fontSize === size.value}
                    >
                      <div className="text-sm">{size.label}</div>
                      <div className="text-xs opacity-75">{size.px}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Visual Settings */}
              <div className="space-y-3">
                <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                  <Eye className="w-5 h-5" />
                  Visual Adjustments
                </h3>

                <ToggleSetting
                  icon={<Moon className="w-5 h-5" />}
                  label="High Contrast Mode"
                  description="Increases contrast for better visibility"
                  checked={settings.highContrast}
                  onChange={checked => updateSettings({ highContrast: checked })}
                />

                <ToggleSetting
                  icon={<Zap className="w-5 h-5" />}
                  label="Reduce Motion"
                  description="Minimizes animations and transitions"
                  checked={settings.reducedMotion}
                  onChange={checked => updateSettings({ reducedMotion: checked })}
                />

                <ToggleSetting
                  icon={<BookOpen className="w-5 h-5" />}
                  label="Dyslexia-Friendly Font"
                  description="Uses OpenDyslexic font for easier reading"
                  checked={settings.dyslexicFont}
                  onChange={checked => updateSettings({ dyslexicFont: checked })}
                />

                <ToggleSetting
                  icon={<Focus className="w-5 h-5" />}
                  label="Enhanced Focus Indicators"
                  description="Shows clearer focus outlines for keyboard navigation"
                  checked={settings.focusIndicators}
                  onChange={checked => updateSettings({ focusIndicators: checked })}
                />
              </div>

              {/* Navigation Settings */}
              <div className="space-y-3">
                <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                  <Keyboard className="w-5 h-5" />
                  Navigation
                </h3>

                <ToggleSetting
                  icon={<Keyboard className="w-5 h-5" />}
                  label="Keyboard Navigation"
                  description="Enable enhanced keyboard shortcuts and navigation"
                  checked={settings.keyboardNavigation}
                  onChange={checked => updateSettings({ keyboardNavigation: checked })}
                />

                <ToggleSetting
                  icon={<Settings className="w-5 h-5" />}
                  label="Screen Reader Optimization"
                  description="Optimizes the interface for screen readers"
                  checked={settings.screenReaderOptimized}
                  onChange={checked => updateSettings({ screenReaderOptimized: checked })}
                />
              </div>

              {/* Keyboard Shortcuts Info */}
              {settings.keyboardNavigation && (
                <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                  <h4 className="font-bold text-blue-900 mb-2">Keyboard Shortcuts</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li><kbd className="px-2 py-1 bg-white rounded font-mono text-xs">Tab</kbd> - Navigate forward</li>
                    <li><kbd className="px-2 py-1 bg-white rounded font-mono text-xs">Shift + Tab</kbd> - Navigate backward</li>
                    <li><kbd className="px-2 py-1 bg-white rounded font-mono text-xs">Enter</kbd> - Activate button/link</li>
                    <li><kbd className="px-2 py-1 bg-white rounded font-mono text-xs">Esc</kbd> - Close dialogs/menus</li>
                    <li><kbd className="px-2 py-1 bg-white rounded font-mono text-xs">?</kbd> - View all shortcuts</li>
                  </ul>
                </div>
              )}

              {/* Reset Button */}
              <button
                onClick={() => {
                  resetSettings();
                  setIsOpen(false);
                }}
                className="w-full py-3 px-4 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
              >
                <RotateCcw className="w-5 h-5" />
                Reset to Defaults
              </button>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 rounded-b-2xl">
              <p className="text-sm text-gray-600 text-center">
                We're committed to making ResuSwipe accessible to everyone.
                <br />
                <a href="/accessibility-statement" className="text-blue-600 hover:underline font-medium">
                  View our Accessibility Statement
                </a>
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

interface ToggleSettingProps {
  icon: React.ReactNode;
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

function ToggleSetting({ icon, label, description, checked, onChange }: ToggleSettingProps) {
  return (
    <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
      <div className="flex items-start gap-3 flex-1">
        <div className="text-gray-600 mt-1">{icon}</div>
        <div>
          <div className="font-medium text-gray-900">{label}</div>
          <div className="text-sm text-gray-600">{description}</div>
        </div>
      </div>
      <button
        role="switch"
        aria-checked={checked}
        aria-label={`Toggle ${label}`}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 ${
          checked ? 'bg-blue-600' : 'bg-gray-300'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );
}
